import { Middleware } from '@reduxjs/toolkit';

import { RootState } from '../store';
import {
  adminChatSyncConversationMessages,
  onAdminChatConversationMessageReceived,
  onAdminChatDisconnected,
  onAdminChatSyncedMessagesReceived,
  onAdminChatUserConnectionSynced,
  setAdminChatConversationMessages,
  setAdminChatConversations,
  setAdminChatLoadMoreNotificationData,
  setAdminChatNewConversation,
  setAdminChatUserNotificationsData,
} from '../store/V2/AdminChatSlice';
import { getSocket } from './socket';
import {
  chatDisconnectSocket,
  initiChatConnection,
} from '../store/V2/ChatSlice';

const adminChatSocketMiddleware: Middleware = (store) => {
  let socket;

  const initializeSocket = () => {
    socket.connect();

    socket.on('disconnect', () => {
      store.dispatch(onAdminChatDisconnected());
    });

    socket.on('error', (error) => {
      console.error('Socket error:', error);
    });

    // Sync user connections
    socket.on('syncAdminChatUserConnection', (data) => {
      store.dispatch(onAdminChatUserConnectionSynced(data));
    });

    // Receive conversations
    socket.on('receiveAdminConversations', (data) => {
      store.dispatch(setAdminChatConversations(data));
    });

    // Receive conversation messages
    socket.on('receiveAdminConversationMessages', (data) => {
      store.dispatch(setAdminChatConversationMessages(data));
    });

    // Receive new conversation
    socket.on('receiveNewAdminConversation', (data) => {
      store.dispatch(setAdminChatNewConversation(data));
    });

    // Receive new conversation message
    socket.on('receiveNewAdminConversationMessage', (message) => {
      store.dispatch(onAdminChatConversationMessageReceived(message));

      const currentState: RootState = store.getState();
      const currentAuthUser = currentState.auth.user;

      if (
        currentAuthUser &&
        message.sender._id !== currentAuthUser._id &&
        message.status.some(
          (item) => item.user === currentAuthUser._id && item.status === 'SAVED'
        )
      ) {
        store.dispatch(
          adminChatSyncConversationMessages({
            messageIds: [message._id],
            status: 'DELIVERED',
          })
        );
      }
    });

    // Receive synced messages
    socket.on('receiveAdminSyncedMessages', (data) => {
      store.dispatch(onAdminChatSyncedMessagesReceived(data));
    });

    // Receive user notification data
    socket.on('receiveAdminUserNotificationData', (data) => {
      store.dispatch(setAdminChatUserNotificationsData(data));
    });

    // Recieve loadmore user notification data
    socket.on('receiveAdminUserNotificationDataLoadMore', (data) => {
      store.dispatch(setAdminChatLoadMoreNotificationData(data));
    });
  };

  const emitEvent = (event, payload) => {
    if (socket && socket.connected) {
      socket.emit(event, payload);
    } else {
      console.warn(`Socket is not connected. Cannot emit event: ${event}`);
    }
  };

  return (next) => (action) => {
    switch (action.type) {
      case initiChatConnection.type:
        if (!socket) {
          socket = getSocket(true);
          initializeSocket();
        }
        break;

      case chatDisconnectSocket.type:
        if (socket) {
          socket.disconnect();
          socket = null;
        }
        break;

      case 'adminChat/adminChatGetConversations':
      case 'adminChat/adminChatGetConversationMessages':
      case 'adminChat/adminChatStartNewConversation':
      case 'adminChat/adminChatSendConversationMessage':
      case 'adminChat/adminChatSyncConversationMessages':
      case 'adminChat/adminChatLoadMoreNotificationData':
        emitEvent(action.type.replace('adminChat/', ''), action.payload);
        break;

      default:
        break;
    }

    return next(action);
  };
};

export default adminChatSocketMiddleware;
